import { Box, CircularProgress, Theme, useMediaQuery } from '@mui/material';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import config from '@/config';
import { AppRouteWrapper } from '@/containers';
import {
  useCheckAccessTokenOnDocumentFocus,
  useIsGrowthbookReady,
  usePersistUtmParameters,
  useSessionRecordings,
  useUrlRegion,
} from '@/hooks';
import {
  initDataDog,
  initGrowthbook,
  initLogRocket,
  initSentry,
} from '@/services';
import {
  checkForExistingTokens,
  selectSessionIsLoading,
  selectSessionUserId,
  useAppDispatch,
} from '@/store';
import { parseRedirectUrl } from '@/utils';

export const AuthSessionLoader = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const sessionIsLoading = useSelector(selectSessionIsLoading);
  const userId = useSelector(selectSessionUserId);

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );

  useEffect(() => {
    dispatch(checkForExistingTokens());
  }, [dispatch]);

  useCheckAccessTokenOnDocumentFocus();

  usePersistUtmParameters();
  useUrlRegion();
  const isGrowthbookReady = useIsGrowthbookReady();

  useEffect(() => {
    window.Intercom('boot', {
      user_id: userId,
      app_id: config.INTERCOM_APP_ID,
      hide_default_launcher: isMobile,
    });
  }, [isMobile, userId]);

  useEffect(() => {
    if (sessionIsLoading) return;

    // Initialize services
    initSentry({ userId });
    initGrowthbook({ userId });
    initLogRocket({ userId });
    initDataDog({ userId });

    // Redirect away if user is already authenticated
    if (userId) {
      const redirectUrl = parseRedirectUrl();
      const url = new URL(redirectUrl);
      navigate(`${url.pathname}${url.search}${url.hash}`, { replace: true });
    }
  }, [userId, sessionIsLoading, navigate]);

  useSessionRecordings();

  return sessionIsLoading || !isGrowthbookReady ? (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}>
      <CircularProgress />
    </Box>
  ) : (
    <AppRouteWrapper />
  );
};
